/* eslint-disable @typescript-eslint/no-use-before-define */
import * as Sentry from '@sentry/browser';
import { v4 as uuidv4 } from 'uuid';
import * as R from 'ramda';
import i18next from 'i18next';

import {
  getCardPayments,
  getCustomer,
  getSalesDocument,
  getCardPaymentsForIntegration,
} from 'reducers/Payments';
import {
  deletePayment,
  markForProcessing,
  unmarkFromProcessing,
} from 'actions/Payments';
import { dismissType, addError, addSuccess } from 'actions/Error';
import { getLoggedInEmployeeID, getClientCode } from 'reducers/Login';
import { getCurrentSalesDocument } from 'reducers/sales';
import { lastInvoiceNumbers } from 'services/localDB';
import { getSelectedPosID } from 'reducers/PointsOfSale';
import { posAddOnMessage, posSendMessage } from 'utils/hooks/useWrapper';
import { getCurrencyCode, getUseCayanCapture } from 'reducers/configs/settings';
import { openTerminalIntegration } from 'actions/integrations/terminal';
import {
  sendPayment,
  retractPayment,
  finished,
  cancelPayments,
} from 'paymentIntegrations/wrapper/messages';
import { wrapperFunctionButtons } from 'paymentIntegrations/wrapper/functions';
import {
  deletePaymentGroup,
  printMerchantCardholderReceipts,
} from 'paymentIntegrations/utils';

/**
 * Main method that gets called by cardPaymentUI to handle ALL card payments
 */

const initRegularPayments = hooks => async (dispatch, getState) => {
  const {
    updateMessage,
    enableButtons,
    cardPayments: allCardPayments,
    resolvePayments,
    rejectPayments,
    processSaleAsReturn,
    beforeDocSave,
  } = hooks;
  const clientCode = getClientCode(getState());
  const useCayanCapture = getUseCayanCapture(getState());
  const transactionType = useCayanCapture ? 'PREAUTH' : 'SALE';
  enableButtons(['cancel']);
  const currencyCode = getCurrencyCode(getState());
  const cardPayments = allCardPayments.filter(
    pmt => processSaleAsReturn || !pmt.paid,
  );
  if (cardPayments.length === 0) {
    resolvePayments();
    return null;
  }

  updateMessage('Connecting to wrapper');

  const todo = [...cardPayments.map((p, i) => ({ ...p, referenceNumber: i }))];
  let done = [];

  const customer = getCustomer(getState());
  const employeeID = getLoggedInEmployeeID(getState());
  const nextInvoiceNumber =
    Number(
      await lastInvoiceNumbers.getItem({
        key: getSelectedPosID(getState()),
      }),
    ) + 1;
  const invoice = {
    ...getCurrentSalesDocument(getState()),
    ...getSalesDocument(getState()),
    number: nextInvoiceNumber,
  };
  const paymentCancelled = async payload => {
    if (payload && payload.referenceNumber) {
      done = done.filter(p => p !== payload.referenceNumber);
    }
    await Promise.all(
      cardPayments.map(p => dispatch(unmarkFromProcessing({ key: p.key }))),
    );
    unbindCallbacks.forEach(cb => cb());
    rejectPayments();
  };
  const sendBtnPressed = posSendMessage('payment:pressButton');
  const unbindCallbacks = [
    posAddOnMessage('payment:setMessage', ({ message }) => {
      updateMessage(message);
    }),
    posAddOnMessage('payment:setButtons', ({ buttons }) => {
      wrapperFunctionButtons.set(
        buttons.map(btn => ({
          ...btn,
          actionOnClick: () => async (dispatch, getState) =>
            sendBtnPressed(btn.name),
        })),
      );
      enableButtons(buttons.filter(btn => btn.enabled).map(btn => btn.name));
    }),
    posAddOnMessage('payment:done', async payload => {
      const {
        approvedAmount,
        authCode,
        cardNumber,
        entryMode,
        referenceNumber,
        dateTime,
        signature,
        cardType,
        paymentType,
        additionalData,
      } = R.pipe(
        R.evolve({
          transactionType: R.toUpper,
        }),
        R.when(
          R.propEq('transactionType', 'REFUND'),
          R.evolve({
            approvedAmount: a => -Number(a),
          }),
        ),
      )(payload);
      const tipAmount = additionalData?.amountDetails?.userTipAmount;
      await beforeDocSave({
        key: todo[0].key,
        type: 'CARD',
        paid: true,
        amount: Number(tipAmount)
          ? (Number(approvedAmount) - Number(tipAmount)).toFixed(2)
          : String(approvedAmount),
        cardType: paymentType || cardType,
        cardNumber,
        signature,
        tipAmount,
        attributes: {
          /** refNo as per documentation */
          refNo: referenceNumber,
          /** authCode as per documentation */
          authCode,
          entryMode,
          dateTime,
          /** payment made via Tap to pay - later, on refunds, can compare if pmnt made with t2p and if t2p is currently enabled */
          isTapToPayPayment: !!todo[0].attributes?.isTapToPayPayment,
        },
      });
      if (Number(tipAmount)) {
        await beforeDocSave({
          key: todo[0].key.concat('2'),
          paid: true,
          type: 'TIP',
          caption: 'TIP (adjustment)',
          amount: -tipAmount,
          cardType: paymentType || cardType,
          attributes: {
            refNo: referenceNumber,
          },
        });
        await beforeDocSave({
          key: todo[0].key.concat('1'),
          paid: true,
          type: 'CARD',
          caption: 'TIP (payment)',
          amount: tipAmount,
          cardType: paymentType || cardType,
          cardNumber,
          attributes: {
            cardIsTip: true,
            authCode,
            paymentType: paymentType || cardType,
            refNo: referenceNumber,
            cardNumber,
          },
        });
      }
      if (additionalData) {
        dispatch(printMerchantCardholderReceipts(additionalData));
      }
      done.push(referenceNumber);
      todo.shift();
      if (todo.length > 0) {
        sendPayment({
          clientCode,
          requestID: uuidv4(),
          payment: todo[0],
          employeeID,
          transactionType,
          customer,
          invoice,
          currencyCode,
          index: cardPayments.length - todo.length,
          total: cardPayments.length,
        });
        return;
      }
      finished();
      unbindCallbacks.forEach(cb => cb());
      resolvePayments();
    }),
    posAddOnMessage('payment:failed', paymentCancelled),
    posAddOnMessage('payment:cancelled', paymentCancelled),
  ];

  sendPayment({
    clientCode,
    requestID: uuidv4(),
    payment: todo[0],
    invoice,
    customer,
    transactionType,
    employeeID,
    currencyCode,
    index: 0,
    total: cardPayments.length,
  });
};

/**
 * On void success, find and delete the relevant payments from POS.
 * For example, a single 3.00 payment (2.00 requested + 1.00 tip) is stored in the POS as 3 payments (2, -1, +1).
 * Therefore, this method will find all relevant payments, check for tips, and based on the refno, remove all of them.
 */
const initVoidPayments = ({
  enableButtons,
  rejectPayments,
  updateMessage,
  cardPayments,
}) => async (dispatch, getState) => {
  enableButtons([]);
  let todo = cardPayments
    .filter(p => p.paid && !p.attributes?.cardIsTip)
    .reverse();
  const employeeID = getLoggedInEmployeeID(getState());

  const total = todo.length;
  if (todo.length) {
    retractPayment({
      payment: todo[0],
      employeeID,
      index: 0,
      total,
    });
  } else {
    return rejectPayments();
  }

  updateMessage(`Voiding payment of ${todo[0].amount}...`);
  return new Promise((done, failed) => {
    const sendBtnPressed = posSendMessage('payment:pressButton');
    const unbindCallbacks = [
      posAddOnMessage('payment:failed', async () => {
        dispatch(dismissType('WrapperIntegration/cancel'));
        dispatch(addError('Voiding payments has failed'));
        unbindCallbacks.forEach(cb => cb());
        await Promise.all(
          todo.map(cpr => dispatch(unmarkFromProcessing({ key: cpr.key }))),
        );
        failed();
      }),
      posAddOnMessage('payment:cancelled', () => {
        const payment = todo[0];
        dispatch(deletePaymentGroup(payment)).then(() => {
          dispatch(
            addSuccess(
              i18next.t(
                'paymentIntegrations:transactionMessages.voidSuccessful',
              ),
              {
                selfDismiss: true,
              },
            ),
          );
        });

        todo = todo.splice(1);

        if (todo.length) {
          retractPayment({
            employeeID,
            payment: todo[0],
            index: total - todo.length,
            total,
          });
        } else {
          dispatch(dismissType('WrapperIntegration/cancel'));
          unbindCallbacks.forEach(cb => cb());
          done();
        }
      }),
      posAddOnMessage('payment:setButtons', ({ buttons }) => {
        wrapperFunctionButtons.set(
          buttons.map(btn => ({
            ...btn,
            actionOnClick: () => async (dispatch, getState) =>
              sendBtnPressed(btn.name),
          })),
        );
        enableButtons(buttons.filter(btn => btn.enabled).map(btn => btn.name));
      }),
    ];
  }).then(
    () => rejectPayments(),
    () => rejectPayments(),
  );
};

const reportSoftlockWithCancel = ({ resolvePayments }) => async (
  dispatch,
  getState,
) => {
  const issueID = Sentry.captureMessage('Wrapper override used to cancel');
  posSendMessage('debug:reportSentry')({ issueID });
  resolvePayments();
};
const reportSoftlockWithSuccess = ({
  cardPayments,
  beforeDocDelete,
  beforeDocSave,
  resolvePayments,
}) => async (dispatch, getState) => {
  const issueID = Sentry.captureMessage(
    'Wrapper override used to force payment success',
  );
  posSendMessage('debug:reportSentry')({ issueID });
  const shouldVoid = cardPayments.some(
    payment => payment.shouldProcess === true,
  );
  const todo = getCardPayments(getState())
    .filter(p => (shouldVoid ? p.paid : !p.paid))
    .reverse();
  todo.forEach(t => {
    if (shouldVoid) {
      beforeDocDelete(t.key);
    } else {
      beforeDocSave({
        key: t.key,
        type: 'CARD',
        paid: true,
        amount: String(t.amount),
        cardType: undefined,
        cardNumber: undefined,
        signature: undefined,
        attributes: {
          /** refNo as per documentation */
          refNo: undefined,
          /** authCode as per documentation */
          authCode: undefined,
          entryMode: undefined,
          dateTime: undefined,
        },
      });
    }
  });
  resolvePayments();
};
const cancelButtonAction = hooks => async (dispatch, getState) => {
  dispatch(cancelPayments(hooks));

  const clientCode = getClientCode(getState());
  if (String(clientCode) === '467402') {
    const i = setTimeout(() => {
      wrapperFunctionButtons.add(debugButtons);
      hooks.enableButtons(debugButtons.map(btn => btn.name));
      remove();
    }, 5 * 1000);
    const handler = () => {
      clearTimeout(i);
      remove();
    };
    const remove = posAddOnMessage('payment:cancelled', handler);
  }
};

const debugButtons = [
  {
    actionOnClick: reportSoftlockWithCancel,
    text: 'force cancel',
    name: 'debug-cancel',
    variant: 'debug',
  },
  {
    actionOnClick: reportSoftlockWithSuccess,
    text: 'force success',
    name: 'debug-success',
    variant: 'debug',
  },
];
const defaultButtons = [
  {
    actionOnClick: cancelButtonAction,
    text: 'Cancel',
    name: 'cancel',
    variant: 'danger',
  },
];

const initPayments = hooks => async (dispatch, getState) => {
  const cardPayments = getCardPaymentsForIntegration('wrapper')(getState());
  wrapperFunctionButtons.set(defaultButtons);
  const shouldVoid = cardPayments.every(
    payment => payment.shouldProcess && payment.paid,
  );
  if (shouldVoid) {
    dispatch(initVoidPayments({ ...hooks, cardPayments }));
  } else {
    dispatch(initRegularPayments({ ...hooks, cardPayments }));
  }
};

const voidPayments = () => async (dispatch, getState) => {
  const cardPayments = getCardPayments(getState());
  if (cardPayments.length) {
    cardPayments.forEach(({ key, paid }) =>
      paid
        ? dispatch(markForProcessing({ key }))
        : dispatch(deletePayment({ key })),
    );
  }
  const finalPayments = getCardPayments(getState());
  if (finalPayments.length) {
    await dispatch(openTerminalIntegration());
  }
  return true;
};

const setup = () => {
  // TODO: Move messageChannel setup to here
};
const { functions } = wrapperFunctionButtons;
export { initPayments, functions, voidPayments, setup };
